import { AccountLinkDTO, MCCAccountLinkDTO } from 'api-models'
import { IntegrationLogo } from '@clickguard/design-system'
import { platformToProvider, ConnectionStatusView } from './connection-helpers'

type AnyLink = AccountLinkDTO & {
	name?: string
	email?: string
	external_id?: string
	account_number?: string
}

/**
 * Credential display label (DEV-2422 §2.7). Two formats, one per type: the platform's
 * OAuth name, or `MCC: {manager account name}`.
 *
 * The static `'MCC Account'` this replaced printed the same title for every MCC on the
 * workspace, so two of them were indistinguishable — the manager name was pushed into
 * the line below, where the email belongs.
 */
export const credentialLabel = (link: AccountLinkDTO): string => {
	if (link.platform === 'GADS') {
		if (link.type !== 'mcc') return 'Google OAuth'
		const name = (link as MCCAccountLinkDTO).name?.trim()
		return name ? `MCC: ${name}` : 'MCC'
	}
	if (link.platform === 'FADS') return 'Meta OAuth'
	if (link.platform === 'BADS') return 'Microsoft OAuth'
	return 'Connection'
}

/**
 * The account that granted the access. Every credential has one, so it is always shown.
 *
 * An MCC's own `name` is the MANAGER ACCOUNT's name, never an email — the email sits on
 * the Google link it authenticates through (`MCCAccountLinkDTO.link.email`, returned by
 * `mapLink`). Meta and Bing carry `email` directly, and a Google OAuth link stores the
 * email AS its `name` (`link-service.ts:135`), which is why the fallback is correct
 * for everything except MCC.
 */
export const credentialEmail = (link: AccountLinkDTO): string => {
	if (link.type === 'mcc') return (link as MCCAccountLinkDTO).link?.email ?? ''
	return (link as AnyLink).email ?? (link as AnyLink).name ?? ''
}

/** External credential id shown in the drawer header. */
export const credentialId = (link: AccountLinkDTO): string =>
	(link as AnyLink).external_id ?? (link as AnyLink).account_number ?? ''

export const getCredentialStatus = (
	link: AccountLinkDTO
): ConnectionStatusView => {
	if (link.status === 'OK') return { text: 'Active', color: 'success' }
	if (link.status === 'UNLINKED') return { text: 'Unlinked', color: 'error' }
	if (link.status === 'INVITED') return { text: 'Invited', color: 'warning' }
	return { text: link.status ?? 'Inactive', color: 'warning' }
}

/** A credential has an issue whenever its status is not healthy. */
export const credentialHasIssue = (link: AccountLinkDTO): boolean =>
	link.status !== 'OK'

/** Platform icon — official mark for the credential's ad platform. */
export const CredentialIcon = ({ link }: { link: AccountLinkDTO }) => (
	<IntegrationLogo provider={platformToProvider(link.platform)} size={24} />
)
